import { PriorityBadge } from '@/components/global/PriorityBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/shadcn/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

interface InlinePriorityCellProps {
    value?: string | null;
    onUpdate?: (value: string) => void;
    className?: string;
    priorities?: Array<{ value: string; label: string; color: string; icon?: string }>;
    readOnly?: boolean;
}

export function InlinePriorityCell({ value, onUpdate, className, priorities, readOnly = false }: InlinePriorityCellProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSelect = async (newValue: string) => {
        if (newValue === value) {
            setIsOpen(false);
            return;
        }

        if (!onUpdate) return;

        setIsLoading(true);
        try {
            await onUpdate(newValue);
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to update priority:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const options = (priorities ?? []).map((priority) => ({ value: priority.value, label: priority.label }));
    const hasOptions = options.length > 0;

    // Read-only mode: just display the badge
    if (readOnly || !onUpdate || !hasOptions) {
        return (
            <div className={cn('flex w-full items-center justify-start px-2 py-1.5', className)}>
                <PriorityBadge priority={value || undefined} priorities={priorities} translationNamespace="company_priorities" />
            </div>
        );
    }

    if (isMobile) {
        return (
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogTrigger asChild>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsOpen(true);
                        }}
                        className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center justify-start', className)}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                        ) : (
                            <PriorityBadge priority={value || undefined} priorities={priorities} translationNamespace="company_priorities" />
                        )}
                    </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
                    <DialogHeader>
                        <DialogTitle>{t('companies.index.columns.priority', 'Priority')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-2">
                        {options.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => handleSelect(option.value)}
                                className="bg-background hover:bg-accent flex w-full items-center justify-start rounded-md border px-3 py-2 text-left"
                            >
                                <PriorityBadge priority={option.value} priorities={priorities} translationNamespace="company_priorities" />
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(true);
                    }}
                    className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center justify-start', className)}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                    ) : (
                        <PriorityBadge priority={value || undefined} priorities={priorities} translationNamespace="company_priorities" />
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
                {options.map((option) => (
                    <DropdownMenuItem key={option.value} onClick={() => handleSelect(option.value)} className="cursor-pointer">
                        <PriorityBadge priority={option.value} priorities={priorities} translationNamespace="company_priorities" />
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
